const PAYLOAD_API_URL = import.meta.env.VITE_PAYLOAD_PUBLIC_SERVER_URL !== undefined
  ? import.meta.env.VITE_PAYLOAD_PUBLIC_SERVER_URL
  : ''

export class PayloadAPI {
  private baseURL: string
  private token: string | null = null

  constructor() {
    this.baseURL = PAYLOAD_API_URL
  }

  setToken(token: string) {
    this.token = token
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}/api${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.token) {
      headers.Authorization = `JWT ${this.token}`
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // Authentication
  async login(email: string, password: string) {
    const response = await this.request<{
      user: any
      token: string
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })

    this.setToken(response.token)
    return response
  }

  async register(data: {
    email: string
    password: string
    name: string
  }) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' })
    this.setToken('')
  }

  async forgotPassword(email: string) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async resetPassword(token: string, password: string) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    })
  }

  async getMe() {
    return this.request('/auth/me')
  }

  // Collections
  async getCollection(collection: string, params?: {
    page?: number
    limit?: number
    sort?: string
    where?: Record<string, any>
    depth?: number
  }) {
    const searchParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
        }
      })
    }

    return this.request(`/${collection}?${searchParams}`)
  }

  async getDocument(collection: string, id: string, depth?: number) {
    const searchParams = depth ? `?depth=${depth}` : ''
    return this.request(`/${collection}/${id}${searchParams}`)
  }

  async createDocument(collection: string, data: any) {
    return this.request(`/${collection}`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateDocument(collection: string, id: string, data: any) {
    return this.request(`/${collection}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteDocument(collection: string, id: string) {
    return this.request(`/${collection}/${id}`, {
      method: 'DELETE',
    })
  }

  // Algorithm specific endpoints
  async getAlgorithms(params?: {
    page?: number
    limit?: number
    category?: string
    developer?: string
    featured?: boolean
    minPrice?: number
    maxPrice?: number
    sortBy?: 'createdAt' | 'rating' | 'price' | 'downloads'
    sortOrder?: 'asc' | 'desc'
  }) {
    return this.getCollection('algorithms', params)
  }

  async getAlgorithm(slug: string) {
    return this.getCollection('algorithms', {
      where: {
        slug: {
          equals: slug,
        },
      },
      depth: 2,
    }).then((response: any) => response.docs[0])
  }

  async getDevelopers(params?: {
    page?: number
    limit?: number
    verified?: boolean
  }) {
    return this.getCollection('developers', params)
  }

  async getDeveloper(slug: string) {
    return this.getCollection('developers', {
      where: {
        slug: {
          equals: slug,
        },
      },
      depth: 2,
    }).then((response: any) => response.docs[0])
  }

  async getReviews(algorithmId: string, params?: {
    page?: number
    limit?: number
    rating?: number
  }) {
    return this.getCollection('reviews', {
      ...params,
      where: {
        algorithm: {
          equals: algorithmId,
        },
        status: {
          equals: 'approved',
        },
        ...params?.where,
      },
    })
  }

  async createReview(data: {
    algorithm: string
    title: string
    content: string
    rating: number
  }) {
    return this.createDocument('reviews', data)
  }

  async getCategories() {
    return this.getCollection('categories', {
      where: {
        type: {
          equals: 'category',
        },
      },
    })
  }

  async getTags() {
    return this.getCollection('categories', {
      where: {
        type: {
          equals: 'tag',
        },
      },
    })
  }

  async purchaseAlgorithm(algorithmId: string, pricingType: string) {
    return this.request('/algorithms/purchase', {
      method: 'POST',
      body: JSON.stringify({ algorithmId, pricingType }),
    })
  }

  async runBacktest(data: {
    algorithm: string
    timeframe: {
      startDate: string
      endDate: string
    }
    parameters: {
      initialCapital: number
      commission: number
      slippage: number
    }
  }) {
    return this.createDocument('backtests', data)
  }

  async getBacktest(id: string) {
    return this.getDocument('backtests', id, 2)
  }

  async getBacktests(algorithmId?: string) {
    return this.getCollection('backtests', algorithmId ? {
      where: {
        algorithm: {
          equals: algorithmId,
        },
      },
    } : undefined)
  }
}

export const payloadAPI = new PayloadAPI()